/**
 * Performance monitoring utilities
 * Implements Core Web Vitals tracking
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from "web-vitals";
import { logger } from "@/utils/logger";
import { trackEvent } from "@/utils/analytics";

interface PerformanceMetrics {
  cls: number | null;
  inp: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
}

const metrics: PerformanceMetrics = {
  cls: null,
  inp: null,
  fcp: null,
  lcp: null,
  ttfb: null,
};

/**
 * Send performance metrics to analytics service
 * Integrated with Google Analytics 4
 */
function sendToAnalytics(metric: Metric) {
  // Log to console in development
  logger.info("[Performance]", metric.name, metric.value, metric.rating);

  // Send to Google Analytics if available
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    trackEvent("web_vital", {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      delta: metric.delta,
    });
  }

  // Store metric
  switch (metric.name) {
    case "CLS":
      metrics.cls = metric.value;
      break;
    case "INP":
      metrics.inp = metric.value;
      break;
    case "FCP":
      metrics.fcp = metric.value;
      break;
    case "LCP":
      metrics.lcp = metric.value;
      break;
    case "TTFB":
      metrics.ttfb = metric.value;
      break;
  }
}

/**
 * Initialize Core Web Vitals tracking
 * Call this in main.tsx after app initialization
 */
export function initPerformanceMonitoring() {
  // Track Core Web Vitals
  onCLS(sendToAnalytics);
  onINP(sendToAnalytics); // INP replaced FID in web-vitals v3+
  onFCP(sendToAnalytics);
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);
}

/**
 * Get current performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  return { ...metrics };
}

/**
 * Check if performance metrics meet thresholds
 * Returns true if all metrics are "good"
 */
export function areMetricsGood(): boolean {
  // Thresholds based on Web Vitals recommendations
  const thresholds = {
    cls: 0.1, // Good: < 0.1, Needs Improvement: 0.1-0.25, Poor: > 0.25
    inp: 200, // Good: < 200ms, Needs Improvement: 200-500ms, Poor: > 500ms (INP replaced FID)
    fcp: 1800, // Good: < 1.8s, Needs Improvement: 1.8-3.0s, Poor: > 3.0s
    lcp: 2500, // Good: < 2.5s, Needs Improvement: 2.5-4.0s, Poor: > 4.0s
    ttfb: 800, // Good: < 800ms, Needs Improvement: 800-1800ms, Poor: > 1800ms
  };

  if (metrics.cls !== null && metrics.cls > thresholds.cls) return false;
  if (metrics.inp !== null && metrics.inp > thresholds.inp) return false;
  if (metrics.fcp !== null && metrics.fcp > thresholds.fcp) return false;
  if (metrics.lcp !== null && metrics.lcp > thresholds.lcp) return false;
  if (metrics.ttfb !== null && metrics.ttfb > thresholds.ttfb) return false;

  return true;
}
